/** @jsxImportSource @emotion/react */

import {
  Card,
  Icon,
  ListItem,
  useTheme,
  UtilityInput,
} from '@quickbit/qb-design-system';
import lunr from 'lunr';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLunr } from 'react-lunr';
import { useNavigate } from 'react-router-dom';
import { Markdown } from 'components';
import { documentation, PageText } from './documentation';

const convertReactNodeToString = (node: React.ReactNode): string => {
  if (typeof node === 'string') {
    return node;
  }
  if (Array.isArray(node)) {
    return node.map(convertReactNodeToString).join(' ');
  }
  if (typeof node === 'object' && node !== null && 'props' in node) {
    const props = (node as any).props;
    if (props && 'children' in props) {
      return convertReactNodeToString(props.children);
    }
    if (props && 'code' in props && typeof props.code === 'string') {
      return props.code as string;
    }
  }
  return '';
};

const paddedDocumentation = documentation.map((doc) => ({
  ...doc,
  plaintextContent: convertReactNodeToString(doc.content),
}));

const index = lunr(function () {
  this.field('title');
  this.field('page');
  this.field('plaintextContent');

  paddedDocumentation.forEach((value) => {
    this.add({
      ...value,
    });
  });
});

const toLunrQuery = (query: string) => {
  // This query will boost exact results, followed by a smaller boost to prefix
  // substring matches, and finally a fuzzy search with a distance of 2
  const r =
    query.length > 0
      ? query
          .split(' ')
          .reduce((acc, cur) => acc + (cur ? `${cur}*^10 ${cur}~1 ` : ''), '')
          .trimEnd()
      : '';
  return r;
};

const documentationStore = paddedDocumentation.reduce((acc, value) => {
  acc[value.id] = value;
  return acc;
}, {} as Record<string, PageText>);

export const Search = () => {
  const navigate = useNavigate();
  const { palette, sizing } = useTheme();
  const [query, setQuery] = useState('');
  const searchResults = useLunr(toLunrQuery(query), index, documentationStore);
  const searchRef = useRef<HTMLInputElement>(null);
  const [showCard, setShowCard] = useState(false);
  const [cardPosition, setCardPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (searchRef.current) {
      // Get the bounding rectangle of the search input
      const rect = searchRef.current.getBoundingClientRect();
      setCardPosition({
        top: rect.top,
        left: rect.right + 48, // The search icon is not included in the ref
      });
    }
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div css={{ position: 'relative' }}>
      <UtilityInput
        inputRef={searchRef}
        value={query}
        onChange={(e) => {
          const value = e.target.value.replaceAll(':', ''); // Lunr got angry with colons
          setQuery(value);
          setShowCard(value.length > 0);
        }}
        onBlur={() => setShowCard(false)}
        onFocus={() => setShowCard(query.length > 0)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            searchRef.current?.blur();
          }
        }}
        placeholder="Search"
        endAdornment={<Icon name="search" />}
      />
      {showCard &&
        searchResults.length > 0 &&
        createPortal(
          <div onMouseDown={(e) => e.preventDefault()}>
            <Card
              css={{
                position: 'absolute',
                top: cardPosition.top,
                left: cardPosition.left,
                zIndex: 1,
                maxHeight: sizing.large,
                overflowY: 'scroll',
              }}
            >
              {searchResults
                .slice(0, Math.min(10, searchResults.length))
                .map((result) => {
                  const pageText = result as PageText & {
                    plaintextContent: string;
                  };
                  const reg = new RegExp(
                    `(${query.split(' ').filter(Boolean).join('|')})`,
                    'gi'
                  );
                  const highlightQueryContent =
                    pageText.plaintextContent.replace(reg, '**$1**');

                  const highlightedTitle = pageText.title.replace(
                    reg,
                    '**$1**'
                  );

                  // Truncate to 50 characters, around the first highlight (that starts with **) if found otherwise from start
                  const firstHighlightIndex =
                    highlightQueryContent.indexOf('**');
                  const truncatedContent =
                    firstHighlightIndex >= 0
                      ? highlightQueryContent.slice(
                          Math.max(firstHighlightIndex - 50, 0),
                          Math.min(
                            firstHighlightIndex + 50,
                            highlightQueryContent.length
                          )
                        )
                      : highlightQueryContent.slice(0, 100);

                  return (
                    <ListItem
                      css={{
                        cursor: 'pointer',
                        '&:hover': {
                          background: palette.grey.x100,
                        },
                      }}
                      key={pageText.id}
                      onClick={() => {
                        setQuery('');
                        navigate(
                          `/docs/${pageText.page}${
                            pageText.subPage ? `/${pageText.subPage}` : ''
                          }#${pageText.id}`,
                          { replace: true }
                        );
                        setTimeout(() => {
                          const element = document.getElementById(pageText.id);
                          if (element) {
                            element.scrollIntoView();
                          }
                        }, 0);
                      }}
                      subtitle={
                        <Markdown
                          markdown={highlightedTitle}
                          styles={{
                            p: { margin: 0 },
                            color: palette.grey.default,
                          }}
                        />
                      }
                      title={
                        <Markdown
                          markdown={truncatedContent}
                          styles={{ p: { margin: 0 } }}
                        />
                      }
                      action={
                        <div
                          css={{
                            transform: 'rotate(180deg)',
                            svg: {
                              fill: 'none',
                              stroke: palette.grey.x400,
                            },
                            cursor: 'pointer',
                          }}
                        >
                          <Icon name="chevron" />
                        </div>
                      }
                    />
                  );
                })}
            </Card>
          </div>,
          document.body
        )}
    </div>
  );
};
